/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import {
  FaUsers,
  FaStore,
  FaChartLine,
  FaCoins,
  FaBoxes,
  FaSearch,
  FaFileExcel,
  FaCheckCircle,
  FaExclamationCircle,
  FaBuilding,
  FaMapMarkerAlt,
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
  FaShieldAlt,
  FaBan,
  FaBullseye,
} from 'react-icons/fa'
import {
  FaChevronLeft,
  FaChevronRight,
  FaAnglesLeft,
  FaAnglesRight,
} from 'react-icons/fa6'
import {
  KpiGrid,
  KpiCard,
  ChartCard,
  ChartCardHeader,
  TableWrapper,
  TableControls,
  SearchInputWrapper,
  ExportButton,
  TableScroll,
  StyledTable,
  PositionBadge,
  StatusBadge,
  PaginationContainer,
  PaginationInfo,
  PaginationControls,
  PageButton,
  PageSizeSelect,
  ProgressBarTrack,
  ProgressBarFill,
  FeaturedDistGrid,
  FeaturedDistKpi,
  InvasionBadge,
  TerritoryAlertBox,
  MultiUfContainer,
  UfSectionHeader,
} from './styles'
import {
  CoberturaAnaliticaResponse,
  CoberturaPorUF,
  CoberturaPorDistribuidor,
  ExportCoverageExcelOptions,
} from './types'
import { formatCurrencyBr } from '../../constantes'
import { useTheme } from 'styled-components'

interface CoveragePotentialTabProps {
  data: CoberturaAnaliticaResponse
  onExportExcel: (options?: ExportCoverageExcelOptions) => void
  selectedDistributorIdFilter?: string | number | null
}

export const CoveragePotentialTab: React.FC<CoveragePotentialTabProps> = ({
  data,
  onExportExcel,
  selectedDistributorIdFilter,
}) => {
  const theme = useTheme() as any
  const isLight = theme?.name === 'light'
  const [viewMode, setViewMode] = useState<'uf' | 'distribuidor'>('uf')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(10)
  const [expandedDistId, setExpandedDistId] = useState<number | null>(null)

  const { nacional, porUf, porDistribuidor } = data

  // Sincronizar expansão automática se o filtro global de distribuidor mudar
  useEffect(() => {
    if (
      selectedDistributorIdFilter &&
      selectedDistributorIdFilter !== 'todos' &&
      porDistribuidor &&
      porDistribuidor.length > 0
    ) {
      const match = porDistribuidor.find(
        (d) => String(d.dist_id) === String(selectedDistributorIdFilter),
      )
      if (match) {
        setExpandedDistId(match.dist_id)
        setViewMode('distribuidor')
      }
    }
  }, [selectedDistributorIdFilter, porDistribuidor])

  // Resetar página e expansão ao trocar de visão
  const handleViewModeChange = (mode: 'uf' | 'distribuidor') => {
    setViewMode(mode)
    setCurrentPage(1)
    setSearchTerm('')
    setExpandedDistId(null)
  }

  const handleSearchChange = (val: string) => {
    setSearchTerm(val)
    setCurrentPage(1)
  }

  const toggleExpand = (distId: number) => {
    setExpandedDistId((prev) => (prev === distId ? null : distId))
  }

  // Filtragem de UFs (ordenadas por atingimento do potencial)
  const ufsFiltradas = useMemo(() => {
    let list = porUf || []
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim()
      list = list.filter(
        (item) =>
          item.uf.toLowerCase().includes(term) ||
          item.estado.toLowerCase().includes(term),
      )
    }
    return [...list].sort((a, b) => {
      if ((b.cobertura_atual > 0) !== (a.cobertura_atual > 0)) {
        return b.cobertura_atual > 0 ? 1 : -1
      }
      const atingA = a.atingimento_potencial_pct || 0
      const atingB = b.atingimento_potencial_pct || 0
      if (atingB !== atingA) return atingB - atingA
      return b.cobertura_atual - a.cobertura_atual
    })
  }, [porUf, searchTerm])

  // Filtragem de Distribuidores (suporte a UF Dominante e Invasão, ordenados por atingimento do potencial)
  const distsFiltrados = useMemo(() => {
    let list = porDistribuidor || []
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim()
      list = list.filter(
        (item) =>
          item.distribuidor.toLowerCase().includes(term) ||
          item.cod_dist.toLowerCase().includes(term) ||
          item.regional.toLowerCase().includes(term) ||
          (item.uf_principal && item.uf_principal.toLowerCase().includes(term)) ||
          item.uf.toLowerCase().includes(term) ||
          (item.ufs_invasao &&
            item.ufs_invasao.some((u) => u.uf.toLowerCase().includes(term))),
      )
    }
    return [...list].sort((a, b) => {
      const atingA = a.atingimento_potencial_pct || 0
      const atingB = b.atingimento_potencial_pct || 0
      if (atingB !== atingA) return atingB - atingA
      return b.cobertura_atual - a.cobertura_atual
    })
  }, [porDistribuidor, searchTerm])

  // Paginação dinâmica
  const totalItems = viewMode === 'uf' ? ufsFiltradas.length : distsFiltrados.length
  const effectivePageSize = pageSize === 0 ? totalItems : pageSize
  const totalPages = Math.max(1, Math.ceil(totalItems / (effectivePageSize || 1)))

  const itemsPaginados = useMemo(() => {
    if (pageSize === 0) {
      return viewMode === 'uf' ? ufsFiltradas : distsFiltrados
    }
    const inicio = (currentPage - 1) * pageSize
    const fim = inicio + pageSize
    return viewMode === 'uf'
      ? ufsFiltradas.slice(inicio, fim)
      : distsFiltrados.slice(inicio, fim)
  }, [viewMode, ufsFiltradas, distsFiltrados, currentPage, pageSize])

  // Top 10 para o gráfico contextual
  const top10Ufs = useMemo(() => {
    return [...porUf].filter((u) => u.cobertura_atual > 0).slice(0, 10)
  }, [porUf])

  const top10Dists = useMemo(() => {
    return [...porDistribuidor].filter((d) => d.cobertura_atual > 0).slice(0, 10)
  }, [porDistribuidor])

  // Configuração do gráfico
  const chartOptions: any = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      background: 'transparent',
      foreColor: '#94a3b8',
      fontFamily: 'inherit',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '50%',
        borderRadius: 4,
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => val.toLocaleString('pt-BR'),
      offsetY: -20,
      style: {
        fontSize: '11px',
        colors: ['#e2e8f0'],
        fontWeight: 600,
      },
    },
    colors: viewMode === 'uf' ? ['#a6c10b'] : ['#a6c10b', '#38bdf8'],
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories:
        viewMode === 'uf'
          ? top10Ufs.map((u) => u.uf)
          : top10Dists.map((d) => d.distribuidor.slice(0, 15)),
      labels: {
        style: {
          colors: isLight ? '#334155' : '#e2e8f0',
          fontSize: '11px',
          fontWeight: 600,
        },
      },
      axisBorder: { color: isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)' },
      axisTicks: { color: isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)' },
    },
    yaxis: {
      title: {
        text: 'Pontos de Venda (PDVs)',
        style: { color: isLight ? '#0284c7' : '#a6c10b', fontWeight: 600 },
      },
      labels: {
        formatter: (val: number) => Math.round(val).toLocaleString('pt-BR'),
        style: { colors: isLight ? '#64748b' : '#94a3b8' },
      },
    },
    tooltip: {
      theme: isLight ? 'light' : 'dark',
      y: {
        formatter: (val: number) => `${val.toLocaleString('pt-BR')} PDVs`,
      },
    },
    grid: {
      borderColor: isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.06)',
      strokeDashArray: 4,
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      labels: { colors: isLight ? '#334155' : '#e2e8f0' },
      markers: { radius: 12 },
    },
  }

  const chartSeries =
    viewMode === 'uf'
      ? [
          {
            name: 'PDVs Atendidos',
            data: top10Ufs.map((u) => u.cobertura_atual),
          },
        ]
      : [
          {
            name: 'Positivação Atual (PDVs)',
            data: top10Dists.map((d) => d.cobertura_atual),
          },
          {
            name: 'Potencial Estimado (PDVs)',
            data: top10Dists.map((d) => d.potencial_pdvs || d.cobertura_atual),
          },
        ]

  const getPositionStyle = (idx: number) => {
    if (idx === 0) return { bg: '#FFD700', text: '#333' }
    if (idx === 1) return { bg: '#C0C0C0', text: '#333' }
    if (idx === 2) return { bg: '#CD7F32', text: '#fff' }
    return { bg: 'rgba(255,255,255,0.1)', text: '#fff' }
  }

  const getProgressColor = (percent: number) => {
    if (percent >= 75) return '#22c55e'
    if (percent >= 45) return '#a6c10b'
    if (percent >= 25) return '#f59e0b'
    return '#ef4444'
  }

  return (
    <>
      {/* Cards de KPIs Principais Nacionais com dados do IBGE */}
      <KpiGrid>
        <KpiCard $borderColor='#a6c10b'>
          <div className='kpi-header'>
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>População Atendida</span>
            <FaUsers style={{ color: '#a6c10b', fontSize: '18px' }} />
          </div>
          <div className='kpi-value' style={{ color: isLight ? '#0f172a' : '#ffffff', fontSize: '26px', fontWeight: 700 }}>
            {(nacional.populacao_brasil || nacional.populacao_atendida || 0).toLocaleString('pt-BR')}
          </div>
          <div className='kpi-footer' style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>Habitantes nas {nacional.ufs_atendidas || 0} UFs Atendidas</span>
          </div>
        </KpiCard>

        <KpiCard $borderColor='#38bdf8'>
          <div className='kpi-header'>
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>PDVs Atendidos</span>
            <FaStore style={{ color: '#38bdf8', fontSize: '18px' }} />
          </div>
          <div className='kpi-value' style={{ color: '#38bdf8', fontSize: '26px', fontWeight: 700 }}>
            {(nacional.cobertura_atual || 0).toLocaleString('pt-BR')}
          </div>
          <div className='kpi-footer' style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>{nacional.ufs_atendidas || 0} de 27 UFs Atendidas</span>
          </div>
        </KpiCard>

        <KpiCard $borderColor='#10b981'>
          <div className='kpi-header'>
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Potencial Estimado</span>
            <FaBullseye style={{ color: '#10b981', fontSize: '18px' }} />
          </div>
          <div className='kpi-value' style={{ color: '#4ade80', fontSize: '26px', fontWeight: 700 }}>
            {(nacional.potencial_pdvs || Math.round((nacional.populacao_brasil || 0) / 1000)).toLocaleString('pt-BR')}
          </div>
          <div className='kpi-footer' style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>
              {nacional.atingimento_potencial_pct !== undefined
                ? `${nacional.atingimento_potencial_pct}% de Atingimento Nacional`
                : '1 PDV a cada 1.000 hab.'}
            </span>
          </div>
        </KpiCard>

        <KpiCard $borderColor='#ec4899'>
          <div className='kpi-header'>
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Densidade Média</span>
            <FaChartLine style={{ color: '#ec4899', fontSize: '18px' }} />
          </div>
          <div className='kpi-value' style={{ color: '#f472b6', fontSize: '26px', fontWeight: 700 }}>
            {nacional.hab_por_pdv > 0
              ? `${nacional.hab_por_pdv.toLocaleString('pt-BR')} hab`
              : 'N/A'}
          </div>
          <div className='kpi-footer' style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>1 PDV a cada {nacional.hab_por_pdv.toLocaleString('pt-BR')} habitantes atendidos</span>
          </div>
        </KpiCard>
      </KpiGrid>

      {/* Gráfico Analítico Contextual */}
      <ChartCard>
        <ChartCardHeader>
          <h3>
            <FaChartLine />{' '}
            {viewMode === 'uf'
              ? 'Top 10 Estados em Cobertura de Clientes'
              : 'Top 10 Distribuidores: Positivação Atual vs Potencial Estimado'}
          </h3>
          <span className='chart-subtitle'>
            {viewMode === 'uf'
              ? 'Distribuição de pontos de venda ativos por estado brasileiro'
              : 'Comparativo de cobertura realizada contra o potencial demográfico estimado da área'}
          </span>
        </ChartCardHeader>
        <ReactApexChart options={chartOptions} series={chartSeries} type='bar' height={280} />
      </ChartCard>

      {/* Tabela Analítica com Expansão Integrada e Paginação */}
      <div style={{ padding: '0 10px' }}>
        <TableWrapper>
          <TableControls>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Seletor Por Estado vs Por Distribuidor */}
              <div
                style={{
                  display: 'flex',
                  background: isLight ? '#f1f5f9' : 'rgba(0, 24, 66, 0.6)',
                  borderRadius: '8px',
                  padding: '3px',
                  border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <button
                  type='button'
                  onClick={() => handleViewModeChange('uf')}
                  style={{
                    background: viewMode === 'uf' ? (isLight ? '#0284c7' : '#a6c10b') : 'transparent',
                    color: viewMode === 'uf' ? '#ffffff' : (isLight ? '#475569' : '#cbd5e1'),
                    border: 'none',
                    padding: '6px 14px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                  }}
                >
                  <FaMapMarkerAlt /> Por Estado (UF)
                </button>
                <button
                  type='button'
                  onClick={() => handleViewModeChange('distribuidor')}
                  style={{
                    background: viewMode === 'distribuidor' ? (isLight ? '#0284c7' : '#a6c10b') : 'transparent',
                    color: viewMode === 'distribuidor' ? '#ffffff' : (isLight ? '#475569' : '#cbd5e1'),
                    border: 'none',
                    padding: '6px 14px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                  }}
                >
                  <FaBuilding /> Por Distribuidor
                </button>
              </div>

              {/* Busca Textual */}
              <SearchInputWrapper>
                <FaSearch />
                <input
                  type='text'
                  placeholder={
                    viewMode === 'uf'
                      ? 'Buscar estado ou UF...'
                      : 'Buscar distribuidor, cód, regional...'
                  }
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </SearchInputWrapper>
            </div>

            <ExportButton
              onClick={() =>
                onExportExcel({
                  viewMode,
                  customDists: distsFiltrados,
                  customUfs: ufsFiltradas,
                  tabName: 'potencial',
                })
              }
              title={
                viewMode === 'distribuidor'
                  ? 'Exportar planilha de cobertura por distribuidor'
                  : 'Exportar planilha de cobertura por estado (UF)'
              }
            >
              <FaFileExcel />
              Exportar Excel
            </ExportButton>
          </TableControls>

          <TableScroll>
            {viewMode === 'uf' ? (
              <StyledTable>
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>Pos</th>
                    <th>UF</th>
                    <th>Estado</th>
                    <th style={{ textAlign: 'right' }}>População IBGE</th>
                    <th style={{ textAlign: 'right', color: isLight ? '#0284c7' : '#38bdf8' }}>PDVs Atendidos</th>
                    <th style={{ textAlign: 'right', color: isLight ? '#15803d' : '#a6c10b' }}>Potencial</th>
                    <th style={{ textAlign: 'right', color: isLight ? '#c2410c' : '#f59e0b' }}>Gap</th>
                    <th style={{ textAlign: 'left', minWidth: '150px' }}>Atingimento %</th>
                    <th style={{ textAlign: 'right', color: isLight ? '#0284c7' : 'inherit' }}>Hab / PDV</th>
                    <th style={{ textAlign: 'right' }}>Faturamento R$</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(itemsPaginados as CoberturaPorUF[]).map((item, idx) => {
                    const globalIdx =
                      pageSize === 0 ? idx : (currentPage - 1) * pageSize + idx
                    const posStyle = getPositionStyle(globalIdx)
                    const pot = item.potencial_pdvs || Math.round((item.populacao || 0) / 1000)
                    const gap = item.gap_pdvs !== undefined ? item.gap_pdvs : Math.max(0, pot - item.cobertura_atual)
                    const percent = item.atingimento_potencial_pct !== undefined ? item.atingimento_potencial_pct : (pot > 0 ? Number(((item.cobertura_atual / pot) * 100).toFixed(1)) : 0)
                    const barColor = getProgressColor(percent)

                    return (
                      <tr key={item.uf}>
                        <td>
                          <PositionBadge bgColor={posStyle.bg} textColor={posStyle.text}>
                            {globalIdx + 1}
                          </PositionBadge>
                        </td>
                        <td style={{ fontWeight: 'bold', color: isLight ? '#15803d' : '#a6c10b' }}>{item.uf}</td>
                        <td>{item.estado}</td>
                        <td style={{ textAlign: 'right' }}>
                          {item.populacao > 0 ? item.populacao.toLocaleString('pt-BR') : 'N/D'}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold', color: isLight ? '#0f172a' : '#fff' }}>
                          {item.cobertura_atual.toLocaleString('pt-BR')}
                        </td>
                        <td style={{ textAlign: 'right', color: isLight ? '#15803d' : '#a6c10b', fontWeight: 600 }}>
                          {pot.toLocaleString('pt-BR')}
                        </td>
                        <td style={{ textAlign: 'right', color: isLight ? '#c2410c' : '#f59e0b' }}>
                          {gap.toLocaleString('pt-BR')}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <ProgressBarTrack style={{ width: '80px', height: '6px' }}>
                              <ProgressBarFill $percent={percent} $color={barColor} />
                            </ProgressBarTrack>
                            <span
                              style={{
                                fontSize: '12px',
                                fontWeight: '700',
                                color: barColor,
                                minWidth: '44px',
                              }}
                            >
                              {percent.toFixed(1)}%
                            </span>
                          </div>
                        </td>
                        <td
                          style={{
                            textAlign: 'right',
                            color: item.hab_por_pdv > 0 ? (isLight ? '#0284c7' : '#38bdf8') : (isLight ? '#64748b' : '#64748b'),
                            fontWeight: 600,
                          }}
                          title={
                            item.hab_por_pdv > 0
                              ? `${item.hab_por_pdv.toLocaleString('pt-BR')} habitantes por PDV atendido (1 PDV a cada ${item.hab_por_pdv.toLocaleString('pt-BR')} pessoas)`
                              : undefined
                          }
                        >
                          {item.hab_por_pdv > 0
                            ? `${item.hab_por_pdv.toLocaleString('pt-BR')} hab`
                            : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {formatCurrencyBr(item.total_valor)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <StatusBadge $status={item.status_cobertura}>
                            {item.status_cobertura === 'Ativo' ? (
                              <FaCheckCircle />
                            ) : (
                              <FaExclamationCircle />
                            )}
                            {item.status_cobertura}
                          </StatusBadge>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </StyledTable>
            ) : (
              <StyledTable>
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>Pos</th>
                    <th>Cód.</th>
                    <th>Distribuidor</th>
                    <th>Regional</th>
                    <th>UF Dominante</th>
                    <th style={{ textAlign: 'right' }}>População Área</th>
                    <th style={{ textAlign: 'right', color: isLight ? '#0284c7' : '#38bdf8' }}>Positivação</th>
                    <th style={{ textAlign: 'right', color: isLight ? '#15803d' : '#a6c10b' }}>Potencial</th>
                    <th style={{ textAlign: 'right', color: isLight ? '#c2410c' : '#f59e0b' }}>Gap</th>
                    <th style={{ textAlign: 'left', minWidth: '150px' }}>Atingimento %</th>
                    <th style={{ textAlign: 'right', color: isLight ? '#0284c7' : 'inherit' }}>Hab / PDV</th>
                    <th style={{ textAlign: 'right' }}>Faturamento R$</th>
                    <th style={{ textAlign: 'center', width: '90px' }}>Detalhes</th>
                  </tr>
                </thead>
                <tbody>
                  {(itemsPaginados as CoberturaPorDistribuidor[]).map((d, idx) => {
                    const globalIdx =
                      pageSize === 0 ? idx : (currentPage - 1) * pageSize + idx
                    const posStyle = getPositionStyle(globalIdx)
                    const percent = d.atingimento_potencial_pct || 0
                    const barColor = getProgressColor(percent)
                    const isExpanded = expandedDistId === d.dist_id

                    return (
                      <React.Fragment key={`${d.dist_id}-${globalIdx}`}>
                        {/* Linha Principal do Distribuidor */}
                        <tr
                          onClick={() => toggleExpand(d.dist_id)}
                          style={{
                            cursor: 'pointer',
                            background: isExpanded
                              ? (isLight ? '#f0fdf4' : 'rgba(166, 193, 11, 0.14)')
                              : undefined,
                            borderLeft: isExpanded
                              ? (isLight ? '4px solid #16a34a' : '4px solid #a6c10b')
                              : undefined,
                            transition: 'background 0.2s',
                          }}
                          title='Clique para expandir/recolher os cards de potencial'
                        >
                          <td>
                            <PositionBadge bgColor={posStyle.bg} textColor={posStyle.text}>
                              {globalIdx + 1}
                            </PositionBadge>
                          </td>
                          <td style={{ color: isLight ? '#475569' : '#94a3b8', fontWeight: isLight ? 600 : 400 }}>{d.cod_dist}</td>
                          <td
                            style={{
                              fontWeight: '600',
                              color: isExpanded
                                ? (isLight ? '#166534' : '#a6c10b')
                                : (isLight ? '#0f172a' : '#fff'),
                            }}
                          >
                            {d.distribuidor}
                          </td>
                          <td>{d.regional}</td>
                          <td>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                flexWrap: 'wrap',
                              }}
                            >
                              <span style={{ color: isLight ? '#15803d' : '#a6c10b', fontWeight: 'bold' }}>
                                {d.uf_principal || d.uf}
                              </span>
                              {d.ufs_invasao && d.ufs_invasao.length > 0 && (
                                <InvasionBadge
                                  $isWarning={d.status_territorio === 'quebra'}
                                  title={
                                    d.status_territorio === 'quebra'
                                      ? d.alerta_territorio
                                      : `Invasão em: ${d.ufs_invasao
                                          .map((u) => `${u.uf} (${u.cobertura})`)
                                          .join(', ')}`
                                  }
                                >
                                  {d.status_territorio === 'quebra' ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', marginRight: '3px' }}>
                                    <FaExclamationTriangle size={10} /> Quebra
                                  </span>
                                ) : (
                                  '+ '
                                )}
                                  {d.ufs_invasao
                                    .map((u) => `${u.uf} (${u.cobertura})`)
                                    .join(', ')}
                                </InvasionBadge>
                              )}
                            </div>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {d.populacao > 0 ? d.populacao.toLocaleString('pt-BR') : 'N/D'}
                          </td>
                          <td
                            style={{
                              textAlign: 'right',
                              fontWeight: 'bold',
                              color: isLight ? '#0284c7' : '#38bdf8',
                            }}
                          >
                            {d.cobertura_atual.toLocaleString('pt-BR')}
                          </td>
                          <td
                            style={{
                              textAlign: 'right',
                              fontWeight: 'bold',
                              color: isLight ? '#166534' : '#a6c10b',
                            }}
                          >
                            {(d.potencial_pdvs || 0).toLocaleString('pt-BR')}
                          </td>
                          <td
                            style={{
                              textAlign: 'right',
                              fontWeight: '600',
                              color: (d.gap_pdvs || 0) > 0 ? (isLight ? '#c2410c' : '#f59e0b') : (isLight ? '#16a34a' : '#22c55e'),
                            }}
                          >
                            {(d.gap_pdvs || 0) > 0
                              ? `+${(d.gap_pdvs || 0).toLocaleString('pt-BR')}`
                              : 'Atingido'}
                          </td>
                          <td style={{ textAlign: 'left' }}>
                            <ProgressBarTrack>
                              <ProgressBarFill $percent={percent} $color={barColor} />
                            </ProgressBarTrack>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '11px',
                                color: isLight
                                  ? (barColor === '#a6c10b'
                                      ? '#166534'
                                      : (barColor === '#f59e0b'
                                          ? '#b45309'
                                          : barColor))
                                  : barColor,
                              }}
                            >
                              {percent.toFixed(1)}%
                            </span>
                          </td>
                          <td
                            style={{
                              textAlign: 'right',
                              color: d.hab_por_pdv > 0 ? (isLight ? '#0284c7' : '#38bdf8') : (isLight ? '#64748b' : '#64748b'),
                              fontWeight: 600,
                            }}
                            title={
                              d.hab_por_pdv > 0
                                ? `${d.hab_por_pdv.toLocaleString('pt-BR')} habitantes por PDV atendido (1 PDV a cada ${d.hab_por_pdv.toLocaleString('pt-BR')} pessoas)`
                                : undefined
                            }
                          >
                            {d.hab_por_pdv > 0
                              ? `${d.hab_por_pdv.toLocaleString('pt-BR')} hab`
                              : '—'}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {formatCurrencyBr(d.total_valor)}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type='button'
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleExpand(d.dist_id)
                              }}
                              style={{
                                background: isExpanded
                                  ? (isLight ? '#16a34a' : '#a6c10b')
                                  : (isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.08)'),
                                color: isExpanded
                                  ? '#ffffff'
                                  : (isLight ? '#334155' : '#cbd5e1'),
                                border: isExpanded
                                  ? (isLight ? '1px solid #16a34a' : '1px solid #a6c10b')
                                  : (isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.15)'),
                                borderRadius: '6px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s',
                              }}
                            >
                              {isExpanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                              {isExpanded ? 'Fechar' : 'Cards'}
                            </button>
                          </td>
                        </tr>

                        {/* Linha Expandida com Cards de Potencial e Gestão de Território */}
                        {isExpanded && (
                          <tr
                            style={{
                              background: isLight
                                ? '#f8fafc'
                                : 'linear-gradient(135deg, rgba(8, 38, 99, 0.5) 0%, rgba(3, 16, 46, 0.7) 100%)',
                            }}
                          >
                            <td
                              colSpan={13}
                              style={{
                                padding: '16px 20px',
                                borderBottom: isLight ? '2px solid #cbd5e1' : '2px solid rgba(166, 193, 11, 0.3)',
                                borderLeft: isLight ? '4px solid #16a34a' : '4px solid #a6c10b',
                              }}
                            >
                              {/* Alerta de Quebra de Território ou Invasão */}
                              {d.status_territorio && d.status_territorio !== 'estavel' && (
                                <TerritoryAlertBox $severity={d.status_territorio}>
                                  <FaExclamationTriangle />
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: 'bold' }}>
                                      {d.alerta_territorio}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: '11px',
                                        opacity: 0.9,
                                        fontWeight: 'normal',
                                      }}
                                    >
                                      {d.status_territorio === 'quebra'
                                        ? 'Atenção Operacional: Este distribuidor está atuando fortemente em território secundário com volume expressivo de clientes. O potencial demográfico NÃO é atribuído a áreas de invasão.'
                                        : 'Vendas pontuais registradas fora da base geográfica principal. Potencial demográfico atribuído exclusivamente à UF Dominante.'}
                                    </span>
                                  </div>
                                </TerritoryAlertBox>
                              )}

                              <div
                                style={{
                                  marginBottom: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  flexWrap: 'wrap',
                                  gap: '8px',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: '14px',
                                      fontWeight: 'bold',
                                      color: isLight ? '#0f172a' : '#a6c10b',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                    }}
                                  >
                                    <FaBullseye size={14} color={isLight ? '#16a34a' : '#a6c10b'} /> Painel Territorial de Potencial: {d.distribuidor}
                                  </span>
                                  <span style={{ fontSize: '11px', color: isLight ? '#475569' : '#94a3b8' }}>
                                    (Cód: {d.cod_dist} | Regional: {d.regional} | Base Principal: {d.uf_principal || d.uf})
                                  </span>
                                </div>
                                <span style={{ fontSize: '11px', color: isLight ? '#64748b' : '#cbd5e1' }}>
                                  Clique na linha novamente para recolher
                                </span>
                              </div>

                              <MultiUfContainer>
                                {/* SEÇÃO 1: TERRITÓRIO DOMINANTE / PRINCIPAL */}
                                <div>
                                  <UfSectionHeader>
                                    <div className='section-title' style={{ color: isLight ? '#0f172a' : '#a6c10b' }}>
                                      <FaMapMarkerAlt color={isLight ? '#16a34a' : '#a6c10b'} /> Território Dominante: {d.uf_principal || d.uf}
                                    </div>
                                    <div className='section-desc' style={{ color: isLight ? '#475569' : undefined }}>
                                      Metas demográficas IBGE vinculadas estritamente à área de concessão principal
                                    </div>
                                  </UfSectionHeader>

                                  <FeaturedDistGrid>
                                    <FeaturedDistKpi $borderColor={isLight ? '#16a34a' : '#a6c10b'}>
                                      <div className='featured-label'>
                                        <span>Potencial Estimado (IBGE)</span>
                                        <FaChartLine />
                                      </div>
                                      <div className='featured-val' style={{ color: isLight ? '#166534' : '#a6c10b' }}>
                                        {(d.potencial_pdvs || 0).toLocaleString('pt-BR')} PDVs
                                      </div>
                                      <div className='featured-sub'>
                                        Meta demográfica em {d.uf_principal || d.uf}
                                      </div>
                                    </FeaturedDistKpi>

                                    <FeaturedDistKpi $borderColor={isLight ? '#0284c7' : '#38bdf8'}>
                                      <div className='featured-label'>
                                        <span>Positivação na Base</span>
                                        <FaStore />
                                      </div>
                                      <div className='featured-val' style={{ color: isLight ? '#0284c7' : '#38bdf8' }}>
                                        {(d.cobertura_uf_principal || d.cobertura_atual || 0).toLocaleString('pt-BR')} PDVs
                                      </div>
                                      <div className='featured-sub'>
                                        Clientes no estado sede ({d.uf_principal || d.uf})
                                      </div>
                                    </FeaturedDistKpi>

                                    <FeaturedDistKpi
                                      $borderColor={(d.gap_pdvs || 0) > 0 ? (isLight ? '#c2410c' : '#f59e0b') : (isLight ? '#16a34a' : '#22c55e')}
                                    >
                                      <div className='featured-label'>
                                        <span>Gap de Cobertura</span>
                                        <FaUsers />
                                      </div>
                                      <div
                                        className='featured-val'
                                        style={{
                                          color: (d.gap_pdvs || 0) > 0 ? (isLight ? '#c2410c' : '#f59e0b') : (isLight ? '#16a34a' : '#22c55e'),
                                        }}
                                      >
                                        {(d.gap_pdvs || 0) > 0
                                          ? `+${(d.gap_pdvs || 0).toLocaleString('pt-BR')} PDVs`
                                          : 'Meta Atingida!'}
                                      </div>
                                      <div className='featured-sub'>
                                        {(d.gap_pdvs || 0) > 0
                                          ? 'Oportunidade na base'
                                          : 'Cobertura plena'}
                                      </div>
                                    </FeaturedDistKpi>

                                    <FeaturedDistKpi $borderColor={barColor}>
                                      <div className='featured-label'>
                                        <span>Atingimento do Potencial</span>
                                        <FaCheckCircle />
                                      </div>
                                      <div
                                        className='featured-val'
                                        style={{
                                          color: isLight
                                            ? (barColor === '#a6c10b'
                                                ? '#166534'
                                                : (barColor === '#f59e0b'
                                                    ? '#b45309'
                                                    : barColor))
                                            : barColor,
                                        }}
                                      >
                                        {percent.toFixed(1)}%
                                      </div>
                                      <div className='featured-sub'>
                                        <ProgressBarTrack style={{ width: '100%' }}>
                                          <ProgressBarFill $percent={percent} $color={barColor} />
                                        </ProgressBarTrack>
                                      </div>
                                    </FeaturedDistKpi>

                                    <FeaturedDistKpi $borderColor='#ec4899'>
                                      <div className='featured-label'>
                                        <span>Base Cadastrada ({d.uf_principal || d.uf})</span>
                                        <FaUsers />
                                      </div>
                                      <div className='featured-val'>
                                        {(d.total_cadastrados || 0).toLocaleString('pt-BR')}
                                      </div>
                                      <div className='featured-sub'>
                                        Ativação: {(d.positivacao_carteira_pct || 0).toFixed(1)}% da carteira
                                      </div>
                                    </FeaturedDistKpi>

                                    <FeaturedDistKpi $borderColor='#8b5cf6'>
                                      <div className='featured-label'>
                                        <span>População {d.uf_principal || d.uf} (IBGE)</span>
                                        <FaMapMarkerAlt />
                                      </div>
                                      <div className='featured-val'>
                                        {d.populacao > 0 ? d.populacao.toLocaleString('pt-BR') : 'N/D'}
                                      </div>
                                      <div className='featured-sub'>
                                        {d.hab_por_pdv > 0
                                          ? `1 PDV a cada ${d.hab_por_pdv.toLocaleString('pt-BR')} hab.`
                                          : 'Densidade N/D'}
                                      </div>
                                    </FeaturedDistKpi>

                                    <FeaturedDistKpi $borderColor={isLight ? '#16a34a' : '#10b981'}>
                                      <div className='featured-label'>
                                        <span>Faturamento na Base</span>
                                        <FaCoins />
                                      </div>
                                      <div className='featured-val' style={{ color: isLight ? '#166534' : undefined }}>
                                        {formatCurrencyBr(
                                          d.ufs_detalhes?.find((u) => u.tipo === 'principal')?.total_valor ||
                                            d.total_valor,
                                        )}
                                      </div>
                                      <div className='featured-sub'>
                                        Volume: {(
                                          d.ufs_detalhes?.find((u) => u.tipo === 'principal')?.total_caixas ||
                                          d.total_caixas ||
                                          0
                                        ).toLocaleString('pt-BR')} cx
                                      </div>
                                    </FeaturedDistKpi>

                                  </FeaturedDistGrid>
                                </div>

                                {/* SEÇÃO 2: TERRITÓRIOS DE INVASÃO / SECUNDÁRIOS */}
                                {d.ufs_detalhes &&
                                  d.ufs_detalhes.filter((u) => u.tipo === 'invasao').length > 0 && (
                                    <div>
                                      <UfSectionHeader>
                                        <div
                                          className='section-title'
                                          style={{
                                            color:
                                              d.status_territorio === 'quebra'
                                                ? (isLight ? '#b91c1c' : '#ef4444')
                                                : (isLight ? '#b45309' : '#f59e0b'),
                                          }}
                                        >
                                          <FaShieldAlt /> Território(s) de Invasão / Secundário(s)
                                        </div>
                                        <div
                                          className='section-desc'
                                          style={{
                                            color: isLight ? '#b91c1c' : '#fca5a5',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                          }}
                                        >
                                          <FaExclamationTriangle size={11} /> Clientes positivados fora do território sede — Potencial proporcional calculado com base na presença demográfica
                                        </div>
                                      </UfSectionHeader>

                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {d.ufs_detalhes
                                          .filter((u) => u.tipo === 'invasao')
                                          .map((invUf) => {
                                            const ehQuebra =
                                              d.status_territorio === 'quebra' &&
                                              (invUf.cobertura_atual >= 100 ||
                                                (d.cobertura_uf_principal > 0 &&
                                                  invUf.cobertura_atual / d.cobertura_uf_principal >= 0.3))

                                            return (
                                              <div
                                                key={invUf.uf}
                                                style={{
                                                  background: isLight ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
                                                  border: isLight
                                                    ? (ehQuebra ? '1px solid #fca5a5' : '1px solid #fed7aa')
                                                    : (ehQuebra
                                                        ? '1px solid rgba(239, 68, 68, 0.4)'
                                                        : '1px solid rgba(245, 158, 11, 0.3)'),
                                                  borderRadius: '8px',
                                                  padding: '14px 16px',
                                                  boxShadow: isLight ? '0 1px 4px rgba(0,0,0,0.05)' : 'none',
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    marginBottom: '10px',
                                                    flexWrap: 'wrap',
                                                    gap: '8px',
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: '8px',
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        fontSize: '13px',
                                                        fontWeight: 'bold',
                                                        color: isLight ? '#0f172a' : '#ffffff',
                                                      }}
                                                    >
                                                      Estado: {invUf.uf} - {invUf.estado}
                                                    </span>
                                                    <InvasionBadge $isWarning={ehQuebra}>
                                                      {ehQuebra
                                                        ? 'QUEBRA DE TERRITÓRIO'
                                                        : 'INVASÃO PONTUAL'}
                                                    </InvasionBadge>
                                                  </div>
                                                  <span style={{ fontSize: '11px', color: isLight ? '#475569' : '#94a3b8' }}>
                                                    Participação:{' '}
                                                    {(
                                                      (invUf.cobertura_atual /
                                                        (d.cobertura_atual || 1)) *
                                                      100
                                                    ).toFixed(1)}
                                                    % dos PDVs totais do distribuidor
                                                  </span>
                                                </div>

                                                <FeaturedDistGrid>
                                                  <FeaturedDistKpi $borderColor={isLight ? '#0284c7' : '#38bdf8'}>
                                                    <div className='featured-label'>
                                                      <span>Positivação Realizada</span>
                                                      <FaStore />
                                                    </div>
                                                    <div
                                                      className='featured-val'
                                                      style={{ color: isLight ? '#0284c7' : '#38bdf8' }}
                                                    >
                                                      {invUf.cobertura_atual.toLocaleString('pt-BR')}{' '}
                                                      PDVs
                                                    </div>
                                                    <div className='featured-sub'>
                                                      Anterior: {invUf.cobertura_anterior} | Saldo:{' '}
                                                      {invUf.saldo_liquido >= 0 ? '+' : ''}
                                                      {invUf.saldo_liquido}
                                                    </div>
                                                  </FeaturedDistKpi>

                                                  <FeaturedDistKpi
                                                    $borderColor={isLight ? '#10b981' : '#10b981'}
                                                  >
                                                    <div className='featured-label'>
                                                      <span>Potencial Estimado</span>
                                                      <FaBullseye />
                                                    </div>
                                                    <div
                                                      className='featured-val'
                                                      style={{ color: isLight ? '#15803d' : '#a6c10b', fontSize: '18px' }}
                                                    >
                                                      {invUf.potencial_pdvs !== null && invUf.potencial_pdvs !== undefined
                                                        ? `${invUf.potencial_pdvs.toLocaleString('pt-BR')} PDVs`
                                                        : 'N/A'}
                                                    </div>
                                                    <div className='featured-sub'>
                                                      {invUf.atingimento_potencial_pct !== null && invUf.atingimento_potencial_pct !== undefined
                                                        ? `${invUf.atingimento_potencial_pct}% atingido`
                                                        : 'Proporcional à presença'}
                                                    </div>
                                                  </FeaturedDistKpi>

                                                  <FeaturedDistKpi $borderColor={isLight ? '#16a34a' : '#10b981'}>
                                                    <div className='featured-label'>
                                                      <span>Faturamento Realizado</span>
                                                      <FaCoins />
                                                    </div>
                                                    <div
                                                      className='featured-val'
                                                      style={{ color: isLight ? '#166534' : '#10b981' }}
                                                    >
                                                      {formatCurrencyBr(invUf.total_valor)}
                                                    </div>
                                                    <div className='featured-sub'>
                                                      Volume:{' '}
                                                      {invUf.total_caixas.toLocaleString('pt-BR')} cx
                                                    </div>
                                                  </FeaturedDistKpi>

                                                  <FeaturedDistKpi $borderColor='#ec4899'>
                                                    <div className='featured-label'>
                                                      <span>Base Cadastrada no UF</span>
                                                      <FaUsers />
                                                    </div>
                                                    <div className='featured-val'>
                                                      {invUf.total_cadastrados.toLocaleString('pt-BR')}
                                                    </div>
                                                    <div className='featured-sub'>
                                                      Ativação:{' '}
                                                      {invUf.total_cadastrados > 0
                                                        ? (
                                                            (invUf.cobertura_atual /
                                                              invUf.total_cadastrados) *
                                                            100
                                                          ).toFixed(1)
                                                        : 0}
                                                      % da carteira
                                                    </div>
                                                  </FeaturedDistKpi>
                                                </FeaturedDistGrid>
                                              </div>
                                            )
                                          })}
                                      </div>
                                    </div>
                                  )}
                              </MultiUfContainer>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </StyledTable>
            )}
          </TableScroll>

          {/* Barra de Paginação Completa */}
          <PaginationContainer>
            <PaginationInfo>
              Mostrando{' '}
              <b>
                {totalItems === 0
                  ? 0
                  : pageSize === 0
                  ? 1
                  : (currentPage - 1) * pageSize + 1}
              </b>{' '}
              a{' '}
              <b>
                {pageSize === 0
                  ? totalItems
                  : Math.min(currentPage * pageSize, totalItems)}
              </b>{' '}
              de <b>{totalItems}</b>{' '}
              {viewMode === 'uf' ? 'estados' : 'distribuidores'}
            </PaginationInfo>

            <PaginationControls>
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>Por página:</span>
              <PageSizeSelect
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={0}>Todos</option>
              </PageSizeSelect>

              {pageSize > 0 && totalPages > 1 && (
                <>
                  <PageButton
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                    title='Primeira Página'
                  >
                    <FaAnglesLeft />
                  </PageButton>
                  <PageButton
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    title='Página Anterior'
                  >
                    <FaChevronLeft />
                  </PageButton>

                  <span
                    style={{
                      fontSize: '12px',
                      color: '#e2e8f0',
                      padding: '0 6px',
                      fontWeight: 600,
                    }}
                  >
                    Página {currentPage} de {totalPages}
                  </span>

                  <PageButton
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    title='Próxima Página'
                  >
                    <FaChevronRight />
                  </PageButton>
                  <PageButton
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    title='Última Página'
                  >
                    <FaAnglesRight />
                  </PageButton>
                </>
              )}
            </PaginationControls>
          </PaginationContainer>
        </TableWrapper>
      </div>
    </>
  )
}
